import { DEFAULT_CURRENCY } from '@app/common/constants';
import { useInventoryMoneyVisibility } from '../../moneyVisibility';
import { LIST_TABLE_APPEARANCE } from '@app/common/listTableAppearance';
import { getNumberFormattingLocaleByLanguage } from '@app/i18n/helpers';
import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import { AppDataTable } from '@app/components/common/AppDataTable';
import { Statistic } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getItemSummary, listMovements } from '../../api';
import type { InventoryItemSummary, InventoryMovement, InventoryMovementRow } from '../../types';
import { getInventoryMovementsTableColumns } from './inventoryMovementsTableColumns.config';

interface InventoryMovementsSubTableProps {
  itemId: number;
}

const InventoryMovementsSubTable: React.FC<InventoryMovementsSubTableProps> = ({ itemId }) => {
  const { t, i18n } = useTranslation();
  const locale = getNumberFormattingLocaleByLanguage(i18n.language);

  const [summary, setSummary] = useState<InventoryItemSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    getItemSummary(itemId)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const fetchMovements = useCallback(
    async (params: { limit: number; offset: number; sort?: string; [key: string]: unknown }) => {
      const { limit, offset } = params;
      const response = await listMovements({ itemId, offset, limit });
      return {
        data: response.data,
        total: response.total || response.data.length,
      };
    },
    [itemId]
  );

  const { showCost, showPrices } = useInventoryMoneyVisibility();

  const tableConfig = useMemo(
    () => ({
      tableId: `inventory-movements-${itemId}`,
      rowKey: 'id' as const,
      // Isolate from the parent table: no URL sync, no localStorage caching.
      storage: { enableUrlSync: false, enableLocalStorage: false },
      // Keep the sub-table minimal: no column-visibility manager / reset button.
      features: { columnVisibility: false },
      fetchData: fetchMovements,
      transformData: (movement: InventoryMovement): InventoryMovementRow => ({
        id: movement.movementId,
        movementType: movement.movementType,
        occurredAt: movement.occurredAt,
        quantity: movement.quantity,
        adjustmentSign: movement.adjustmentSign,
        unitCost: movement.unitCost,
        unitPrice: movement.unitPrice,
        rentalRate: movement.rentalRate,
        rentalRateUnit: movement.rentalRateUnit,
        recipientType: movement.recipientType,
        recipientId: movement.recipientId,
        recipientName: movement.recipientName,
        billingTransactionId: movement.billingTransactionId,
        notes: movement.notes,
      }),
      columns: getInventoryMovementsTableColumns(t, locale, showCost || showPrices),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemId, fetchMovements, t, i18n.language, locale, showCost, showPrices]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-6 rounded-md bg-gray-50 p-4">
        <Statistic title={t('inventory-summary-bought')} value={summary?.purchasedQty ?? 0} />
        {showCost && (
          <Statistic
            title={t('inventory-summary-bought-cost')}
            value={currencyFormatter(summary?.purchasedCost ?? 0, DEFAULT_CURRENCY, locale)}
          />
        )}
        <Statistic title={t('inventory-summary-sold')} value={summary?.soldQty ?? 0} />
        {showPrices && (
          <Statistic
            title={t('inventory-summary-sold-revenue')}
            value={currencyFormatter(summary?.soldRevenue ?? 0, DEFAULT_CURRENCY, locale)}
          />
        )}
        <Statistic title={t('inventory-summary-given')} value={summary?.givenQty ?? 0} />
        <Statistic title={t('inventory-summary-rented-out')} value={summary?.rentedOutQty ?? 0} />
        <Statistic title={t('inventory-summary-on-hand')} value={summary?.onHand ?? 0} />
      </div>
      <AppDataTable appearance={LIST_TABLE_APPEARANCE} config={tableConfig} />
    </div>
  );
};

export default InventoryMovementsSubTable;
